import { Link2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import {
  conversionPercentage,
  progressPercentage,
  trackingLinkFunnel,
} from "../trackingLinks/trackingLinkAnalytics";
import type { TrackingLinkOperationalSummary } from "./funnelSummary";

export function TrackingLinkOperationalFunnel({
  summary,
  isPending,
  error,
  geographyFiltered,
}: {
  summary: TrackingLinkOperationalSummary;
  isPending: boolean;
  error: unknown;
  geographyFiltered: boolean;
}) {
  const stages = trackingLinkFunnel(summary);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="size-4 text-muted-foreground" aria-hidden="true" />
          Tracking-link activity
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Exact link touches in this time range, including returning and
          internal/test users. This operational funnel is separate from the
          first-touch onboarding cohorts below.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <p className="text-sm text-destructive">
            Could not load tracking-link activity.
          </p>
        ) : isPending ? (
          <p className="text-sm text-muted-foreground">Loadingâ€¦</p>
        ) : (
          <div className="space-y-3">
            {stages.map((stage) => {
              const percentage = conversionPercentage(stage.value, stage.total);
              return (
                <div
                  key={stage.key}
                  className="grid grid-cols-[9rem_minmax(0,1fr)_7rem] items-center gap-3"
                >
                  <span className="text-sm">{stage.label}</span>
                  <Progress
                    value={progressPercentage(stage.value, stage.total)}
                    aria-label={`${stage.label}: ${stage.value}`}
                  />
                  <span className="text-right text-sm tabular-nums">
                    {stage.value.toLocaleString()}
                    {stage.total > 0
                      ? ` (${Math.round(percentage * 10) / 10}%)`
                      : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          A returning contact keeps their original acquisition source, but an
          Echo carrying a tracking-link code is counted here. Time-window
          boundaries can make a later stage exceed clicks in the same window.
          {geographyFiltered
            ? " The continent filter applies only to the contact cohorts below; link events do not carry contact geography."
            : ""}
        </p>
      </CardContent>
    </Card>
  );
}
